import {React, observer, useLocalStore, useEffect, useAsObservableSource, Fragment} from '@api'
import {ContextMenu2, ContextMenu2ChildrenProps, ContextMenu2RenderProps} from '@api'
import {GroupByGroupContextMenu} from '@modules/contextMenus'
import {ui} from '@stores'
import {useAssortmentCardStore, useVipLocalStore, VipLocalStore} from '@modules'
import {GroupByGroupStore, GroupByGroupStoreContext, GroupByGroupHeader, GroupByGroupContents, IGroupByQueryManagerResult} from './'
import * as css from './GroupByGroup.css'

export interface GroupByGroupProps {
  groupName: string
  /**
   * The specific group instance (a unique id)
   */
  group: string
  path?: string[]
  loading?: boolean
  inGridCell?: boolean
  level?: number
  /**
   * How many groups (this + siblings) exist for this level of the hierarchy?
   */
  groupCount: number
  index?: number
  hideChildren?: boolean
  attract?: boolean
  info?: IGroupByQueryManagerResult
  firstExpandedInRow?: boolean
  tabIndex?: number
  className?: string
  mustTerms?: Array<any> // Extra ES terms to select your parent path
  mustNotTerms?: Array<any> // Extra ES terms to select your parent path
}

export const GroupByGroup = observer<GroupByGroupProps>(function GroupByGroup(props) {
  const assortmentCard = useAssortmentCardStore(),
    obsProps = useAsObservableSource({assortmentCard, ...props}),
    store = useVipLocalStore((ws) => new GroupByGroupStore(ws, obsProps)),
    {
      groupBy, catalog, onContextMenu, isExpanded, onSetRef, ddHandler, viewMode,
      catalog: {settings: {groupBy: {showEmptyGroups}, showOnlyOfflineItems}},
      grouping: {isLeaf, fieldDefinition}, isCard, props: {level, index, info, inGridCell}, levelOfDetail, settings, isSpec, contextualHoverItemId, onDoubleClick,
    } = store,
    inExpandedCardRow = isCard && isExpanded && isSpec && isLeaf

  useEffect(() => store.registerEffects(), [])
  useEffect(() => {
    !isExpanded && store.collapse()
  }, [isExpanded])

  useEffect(() => {
    if (store.childGroupStore) {
      return store.childGroupStore.mgr.registerEffects()
    }
  }, [store.childGroupStore])


  //console.log(toJS(info))
  if (!showEmptyGroups && info && ((info.count + info.offline_count == 0) || (showOnlyOfflineItems && !info.offline_count))) return null

  if (store.isThisGroupExplicitlyExcluded) return null

  const groupHtml = (
    <ContextMenu2
      content={<GroupByGroupContextMenu />}
    >
      {(props: ContextMenu2ChildrenProps) => {
        return (
          <Fragment>
            {groupBy.pivotView && level > 0 && store.rootStore.props.index > 0 ? null : <GroupByGroupHeader onSetRef={inGridCell || inExpandedCardRow ? undefined : onSetRef} contextMenuProps={props}/>
            }

            {inExpandedCardRow
             ? <div className={css.right}>
               <GroupByGroupContents/>
             </div>
             : <GroupByGroupContents/>}

            <div className={css.filler} data-type='filler' {...(ddHandler?.events ?? {}) }/>
          </Fragment>)
      }}
    </ContextMenu2>)


  return (
    <GroupByGroupStoreContext.Provider value={store}>
      {inGridCell ? <div className={css.cell}
                         data-view-mode={viewMode}
                         ref={onSetRef}
                         data-cell={true}
                         data-level={store.grouping?.level ?? 0}
                         data-leaf={store.grouping.isLeaf}
                         data-expanded={store.isExpanded}
                         style={{ '--item-count': store.childGroupStore?.childGroups.length ?? store.totalCount} as any}
                         data-lod={levelOfDetail}
                         data-group={store.props.info.key}
                         data-contextual-hover-item={contextualHoverItemId}
                         onDoubleClick={onDoubleClick}
                         data-full-width={settings?.fullWidth || (isExpanded && !ui.settings.allowGridChanges) || catalog.settings.viewMode === 'table'}
                         data-single-line={settings?.singleLine}
                  >{groupHtml}</div>
                  : inExpandedCardRow ?
                    <div className={css.cardGroupExpandedRow}
                         ref={onSetRef}
                         data-index={index}
                         data-leaf={isLeaf}
                         data-contextual-hover-item={contextualHoverItemId}
                         data-type='cardGroupExpandedRow'
                         onDoubleClick={onDoubleClick}
                         onContextMenu={onContextMenu}
                         {...(fieldDefinition?.group?.groupDomAttributes ?? {})}
                         data-product-view={catalog.settings.viewMode}>
                      {groupHtml}
                    </div> : groupHtml}
    </GroupByGroupStoreContext.Provider>
  )
})

GroupByGroup.displayName = 'GroupByGroup'
GroupByGroup['whyDidYouRender'] = true

GroupByGroup.defaultProps = {
  path: [],
  group: '',
  level: 0
}



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_Groups/GroupByCatalog_LoadMore/GroupByGroup.tsx